// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Daily Catch  (one landing on pages/DailyCatch.tsx)
// A lot of one menu catch that came in today: real credited photo, when it landed (IST), the boat / spec / grade staff
// entered, and the packs left (alert colour at LOW_STOCK or below, "Sold out" at 0). Pack prices are the menu's own
// per-kg price × pack size (data/packs.ts) — a landing never changes a price. The button opens the same pack-size +
// cut sheet as the menu, capped at the packs left. If the cart already holds more than is left the card says so.
// Deliberately NOT here: "Formalin-tested", "Grade A+" claims not entered by staff, savings, ratings.
// ─────────────────────────────────────────────────────────────────────────────
import { Anchor, Check, Clock, Pencil, Plus, TriangleAlert } from "lucide-react";
import { PACK_SIZES, packPrice, type PackId } from "../../data/packs";
import type { CutOption } from "../../data/cuts";
import type { SeafoodItem } from "../../data/seafood";
import { formatInr } from "../../utils/priceFormat";
import { formatLanded, LOW_STOCK, type Landing } from "../../utils/drop";
import { getWikimediaSrcSet } from "../../utils/wikimediaImage";

interface LandingCardProps {
  landing: Landing;
  item: SeafoodItem;
  inCart: Record<PackId, number>;
  cut?: CutOption;
  onOpen: () => void;
}

export default function LandingCard({ landing, item, inCart, cut, onOpen }: LandingCardProps) {
  const inCartPacks = PACK_SIZES.reduce((sum, pack) => sum + inCart[pack.id], 0);
  const summary = PACK_SIZES.filter((pack) => inCart[pack.id] > 0)
    .map((pack) => `${inCart[pack.id]} × ${pack.label}`)
    .join(" + ");
  const low = !landing.soldOut && landing.packsLeft <= LOW_STOCK;
  const over = inCartPacks > landing.packsLeft;
  const pill = "inline-flex items-center gap-1 rounded-full bg-ocean-700/5 px-2.5 py-1 text-xs font-semibold text-ocean-900 dark:bg-white/10 dark:text-foam-100";

  return (
    <li className={`overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10 ${landing.soldOut ? "opacity-80" : ""}`}>
      <div className="relative">
        <img
          src={item.image}
          srcSet={getWikimediaSrcSet(item.image)}
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          alt={item.name}
          title={item.credit}
          loading="lazy"
          className={`h-52 w-full object-cover ${landing.soldOut ? "grayscale" : ""}`}
        />
        <ul aria-label="Landing details" className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          <li className="inline-flex items-center gap-1 rounded-full bg-ocean-900/90 px-2.5 py-1 text-xs font-bold text-white">
            <Clock className="h-3 w-3" aria-hidden="true" /> Landed {formatLanded(landing.landedAt)}
          </li>
          {landing.boat && (
            <li className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold text-ocean-900">
              <Anchor className="h-3 w-3" aria-hidden="true" /> {landing.boat}
            </li>
          )}
        </ul>
        <span
          className={`absolute bottom-3 right-3 rounded-full px-3 py-1 text-xs font-bold shadow-sm ${
            landing.soldOut ? "bg-ocean-900 text-white" : low ? "bg-coral-500 text-white" : "bg-white/95 text-ocean-900"
          }`}
        >
          {landing.soldOut ? "Sold out" : low ? `Only ${landing.packsLeft} ${landing.packsLeft === 1 ? "pack" : "packs"} left` : `${landing.packsLeft} packs left`}
        </span>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold leading-snug text-ocean-950 dark:text-foam-50">{item.name}</h3>
          {landing.grade && <span className="shrink-0 rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-bold text-teal-700">Grade {landing.grade}</span>}
        </div>
        <p className="text-xs italic text-ocean-800/75 dark:text-foam-100/70">{item.scientificName}</p>
        <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">{item.description}</p>
        {landing.spec && <p className="mt-2"><span className={pill}>{landing.spec}</span></p>}

        <dl className="mt-3 grid grid-cols-2 gap-2 text-center">
          {PACK_SIZES.map((pack) => (
            <div key={pack.id} className="rounded-xl bg-ocean-700/5 px-3 py-2 dark:bg-white/10">
              <dt className="text-sm font-bold text-ocean-950 dark:text-foam-50">{pack.label}</dt>
              <dd className="text-sm text-ocean-800/85 dark:text-foam-100/80">{formatInr(packPrice(item, pack))}</dd>
            </div>
          ))}
        </dl>

        {over && (
          <p role="alert" className="mt-3 flex items-start gap-1.5 rounded-lg bg-coral-400/10 px-3 py-2 text-xs font-semibold text-coral-500 dark:text-coral-300">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Your cart has {inCartPacks} {inCartPacks === 1 ? "pack" : "packs"} but only {landing.packsLeft} {landing.packsLeft === 1 ? "is" : "are"} left. Edit to keep your order.
          </p>
        )}
        {!over && inCartPacks > 0 && (
          <p className="mt-3 flex items-center gap-1 text-xs font-semibold text-teal-700 dark:text-teal-300">
            <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            In cart: {summary}{cut ? ` · ${cut.name}` : ""}
          </p>
        )}

        <button
          type="button"
          onClick={onOpen}
          disabled={landing.soldOut && inCartPacks === 0}
          aria-label={landing.soldOut ? `${item.name} is sold out` : inCartPacks > 0 ? `Edit ${item.name} in your cart` : `Add ${item.name} to cart`}
          className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-full bg-ocean-800 px-5 text-sm font-bold text-white transition-colors hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:bg-ocean-700/30 disabled:text-ocean-900/60"
        >
          {inCartPacks > 0 ? <Pencil className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
          {landing.soldOut ? (inCartPacks > 0 ? "Edit cart" : "Sold out") : inCartPacks > 0 ? "Edit" : "Add to Cart"}
        </button>
      </div>
    </li>
  );
}
